import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Home, Settings as SettingsIcon, Building2, Inbox, MapPin, Landmark, X } from "lucide-react";
import api, { imageUrl } from "../lib/api";
import { priceLabel, typeLabel } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../hooks/useSettings";
import { useQueryClient } from "@tanstack/react-query";
import Seo from "../components/Seo";
import LeadsPanel from "../components/admin/LeadsPanel";
import LocalitiesPanel from "../components/admin/LocalitiesPanel";
import ProjectsPanel from "../components/admin/ProjectsPanel";

const AdminDashboard = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: settings } = useSettings();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("properties");
  const [form, setForm] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/properties", { params: { limit: 60, sort: "newest" } })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (settings) setForm(settings); }, [settings]);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    await api.delete(`/properties/${p._id}`);
    toast.success("Property deleted");
    load();
  };

  const doLogout = () => { logout(); navigate("/"); };

  const saveSettings = async (e) => {
    e.preventDefault();
    await api.put("/settings", form);
    qc.invalidateQueries({ queryKey: ["settings"] });
    toast.success("Settings saved");
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <Seo title="Admin Dashboard | Satyam Real Estate" description="Manage properties" />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl text-stone-900">Dashboard</h1>
          <p className="text-stone-500 text-sm mt-1">Welcome, {admin?.name || admin?.email}</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" className="text-sm text-stone-600 hover:text-brand flex items-center gap-1.5"><Home size={16} /> View Site</Link>
          <button onClick={doLogout} data-testid="logout-btn"
            className="flex items-center gap-1.5 rounded-full border border-stone-200 px-4 py-2 text-sm font-medium hover:border-brand transition-colors">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-8 border-b border-stone-200">
        {[{ k: "properties", l: "Properties", i: Building2 }, { k: "projects", l: "Projects", i: Landmark }, { k: "leads", l: "Enquiries", i: Inbox }, { k: "localities", l: "Locality Guides", i: MapPin }, { k: "settings", l: "Contact Settings", i: SettingsIcon }].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.k ? "border-brand text-brand" : "border-transparent text-stone-500 hover:text-stone-800"}`}>
            <t.i size={16} /> {t.l}
          </button>
        ))}
      </div>

      {tab === "properties" && (
        <>
          <div className="flex items-center justify-between mb-5">
            <p className="text-stone-600 text-sm">{total} total listings</p>
            <Link to="/admin/properties/new" data-testid="add-property-btn"
              className="flex items-center gap-2 rounded-full bg-brand text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand-dark transition-colors">
              <Plus size={16} /> Add Property
            </Link>
          </div>
          {loading ? (
            <div className="h-40 rounded-xl bg-stone-100 animate-pulse" />
          ) : (
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="properties-table">
                  <thead>
                    <tr className="text-left text-stone-500 border-b border-stone-200 bg-stone-50">
                      <th className="p-4 font-medium">Property</th>
                      <th className="p-4 font-medium">Type</th>
                      <th className="p-4 font-medium">Price</th>
                      <th className="p-4 font-medium">Status</th>
                      <th className="p-4 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((p) => (
                      <tr key={p._id} className="border-b border-stone-100 last:border-0" data-testid={`row-${p.slug}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <img src={imageUrl(p.images?.[0]) || undefined} alt="" className="w-14 h-11 rounded-lg object-cover bg-stone-100" />
                            <div>
                              <p className="font-medium text-stone-800 line-clamp-1">{p.title}</p>
                              <p className="text-xs text-stone-500">{p.locality}, {p.city}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-stone-600">{typeLabel(p.property_type)}</td>
                        <td className="p-4 font-semibold text-stone-800">{priceLabel(p)}</td>
                        <td className="p-4">
                          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                            p.status === "available" ? "bg-green-100 text-green-700" : "bg-stone-200 text-stone-600"}`}>
                            {p.status}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-2">
                            <Link to={`/admin/properties/${p._id}/edit`} data-testid={`edit-${p.slug}`}
                              className="p-2 rounded-lg hover:bg-stone-100 text-stone-600"><Pencil size={16} /></Link>
                            <button onClick={() => remove(p)} data-testid={`delete-${p.slug}`}
                              className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {tab === "leads" && <LeadsPanel />}
      {tab === "projects" && <ProjectsPanel />}
      {tab === "localities" && <LocalitiesPanel />}

      {tab === "settings" && form && (
        <form onSubmit={saveSettings} className="bg-white rounded-2xl border border-stone-200 p-6 max-w-2xl space-y-4" data-testid="settings-form">
          {[
            { k: "firm_name", l: "Firm Name" },
            { k: "tagline", l: "Tagline" },
            { k: "whatsapp", l: "WhatsApp Number (with country code, e.g. 919876543210)" },
            { k: "phone", l: "Phone Number" },
            { k: "email", l: "Email" },
            { k: "address", l: "Office Address" },
          ].map((f) => (
            <div key={f.k}>
              <label className="text-sm font-medium text-stone-700">{f.l}</label>
              <input value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`settings-${f.k}`}
                className="mt-1.5 w-full rounded-lg border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
            </div>
          ))}
          <div>
            <label className="text-sm font-medium text-stone-700">About</label>
            <textarea rows={4} value={form.about || ""} onChange={(e) => setForm({ ...form, about: e.target.value })} data-testid="settings-about"
              className="mt-1.5 w-full rounded-lg border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
          </div>
          <button type="submit" data-testid="save-settings-btn"
            className="rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-dark transition-colors">
            Save Settings
          </button>
        </form>
      )}
    </div>
  );
};

export default AdminDashboard;
